import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Card, EmptyState, Field, Loading, ModuleHeader, SearchSelect, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { IconMail } from '../components/icons';

/**
 * Eingang für Meldungen von Dritten (Kap. 5) – Mieter, Nachbarn, Bewohner
 * ohne Benutzerkonto. Der Server liefert hier nur, was noch niemand
 * gesichtet hat (`reporter_id IS NULL AND status = 'new'`); sobald eine
 * Zeile übernommen oder verworfen wird, verlässt sie diese Liste von allein.
 */
export function InboxPage() {
  const inbox = useAsync(() => api.get('/api/tasks/inbox'), []);
  const catalogs = useAsync(() => api.get('/api/catalogs'), []);
  const properties = useAsync(() => api.get('/api/properties'), []);
  const users = useAsync(() => api.get('/api/users'), []);

  if (inbox.loading || catalogs.loading || properties.loading || users.loading) return <Loading />;

  const items = inbox.data?.items ?? [];

  return (
    <div className="stack measure">
      <ModuleHeader
        icon={<IconMail width={22} height={22} />}
        title="Eingang"
        subtitle={
          <>
            Meldungen von Dritten ohne Benutzerkonto – ergänzen, zuweisen oder verwerfen. Übernommene und
            verworfene Meldungen verschwinden von selbst aus dieser Liste.
          </>
        }
        pill="Eingang"
        tone="info"
      />

      {items.length === 0 ? (
        <EmptyState title="Eingang leer" hint="Aktuell liegen keine ungesichteten Meldungen von Dritten vor." />
      ) : (
        <div className="stack" style={{ gap: 12 }}>
          {items.map((task: any) => (
            <InboxRow
              key={task.id}
              task={task}
              properties={properties.data ?? []}
              categories={catalogs.data?.categories ?? []}
              criticalityLevels={catalogs.data?.criticalityLevels ?? []}
              users={users.data ?? []}
              onDone={() => inbox.reload()}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InboxRow({
  task,
  properties,
  categories,
  criticalityLevels,
  users,
  onDone,
}: {
  task: any;
  properties: any[];
  categories: any[];
  criticalityLevels: any[];
  users: any[];
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { notify } = useToast();

  const [propertyId, setPropertyId] = useState(String(task.property_id ?? ''));
  const [categoryId, setCategoryId] = useState(String(task.category_id ?? ''));
  const [criticalityId, setCriticalityId] = useState(String(task.criticality_id ?? ''));
  const [assigneeId, setAssigneeId] = useState('');
  const [busy, setBusy] = useState(false);

  const mayEdit = can('tasks.update');
  const mayAssign = can('dispatch.assign');

  const reporterDisplayName = [task.reporter_first_name, task.reporter_name].filter(Boolean).join(' ') || 'Unbekannt';
  const reporterReachability =
    [task.reporter_phone, task.reporter_email].filter(Boolean).join(' · ') || task.reporter_contact || null;

  const takeOver = async () => {
    setBusy(true);
    try {
      if (mayEdit) {
        await api.patch(`/api/tasks/${task.id}`, {
          propertyId: propertyId ? Number(propertyId) : undefined,
          categoryId: categoryId ? Number(categoryId) : undefined,
          criticalityId: criticalityId ? Number(criticalityId) : undefined,
        });
      }
      if (assigneeId && mayAssign) {
        await api.post(`/api/tasks/${task.id}/assign`, { assigneeId: Number(assigneeId) });
      } else {
        await api.post(`/api/tasks/${task.id}/status`, { status: 'triaged' });
      }
      notify(`${task.task_no} übernommen.`, 'success');
      onDone();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Übernahme nicht möglich.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    try {
      await api.post(`/api/tasks/${task.id}/status`, { status: 'cancelled', comment: 'Im Eingang verworfen' });
      notify(`${task.task_no} verworfen.`, 'success');
      onDone();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Verwerfen nicht möglich.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card noBody>
      <div className="stack" style={{ padding: 16, gap: 14 }}>
        <div className="row between" style={{ alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <span className="mono small subtle">{task.task_no}</span>
              <Badge variant="info">{task.channel_name ?? 'Unbekannter Kanal'}</Badge>
              <span className="small subtle">{formatDateTime(task.created_at)}</span>
            </div>
            <div style={{ fontWeight: 600, fontSize: 15, marginTop: 4 }}>{task.title}</div>
            {task.description && (
              <div className="small subtle" style={{ marginTop: 2, maxWidth: 480 }}>
                {task.description}
              </div>
            )}
            {task.location_detail && <div className="small subtle">Ort: {task.location_detail}</div>}
          </div>
          <div className="small" style={{ textAlign: 'right', flexShrink: 0 }}>
            <div>
              <strong>{reporterDisplayName}</strong>
            </div>
            {reporterReachability ? (
              <div className="subtle">{reporterReachability}</div>
            ) : (
              <div className="subtle">Keine Kontaktdaten hinterlegt</div>
            )}
          </div>
        </div>

        <div className="form-grid">
          <Field label="Objekt">
            <SearchSelect
              value={propertyId}
              onChange={setPropertyId}
              disabled={!mayEdit}
              options={properties.map((property) => ({ value: String(property.id), label: `${property.name} (${property.city})` }))}
              placeholder={task.property_name ?? 'Objekt wählen'}
            />
          </Field>
          <Field label="Kategorie / Gewerk">
            <SearchSelect
              value={categoryId}
              onChange={setCategoryId}
              disabled={!mayEdit}
              options={categories.map((category) => ({ value: String(category.id), label: String(category.name) }))}
              placeholder={task.category_name ?? 'Kategorie wählen'}
            />
          </Field>
          <Field label="Kritikalität">
            <SearchSelect
              value={criticalityId}
              onChange={setCriticalityId}
              disabled={!mayEdit}
              options={criticalityLevels.map((level) => ({ value: String(level.id), label: `${level.level}. ${level.name}` }))}
              placeholder={task.criticality_name ?? 'Kritikalität wählen'}
            />
          </Field>
          <Field label="Zuständig">
            <SearchSelect
              value={assigneeId}
              onChange={setAssigneeId}
              disabled={!mayAssign}
              options={users.map((person) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
              placeholder="Nicht zugewiesen"
            />
          </Field>
        </div>

        <div className="row between">
          <button className="btn sm ghost" onClick={() => navigate(`/tasks/${task.id}`)}>
            Details öffnen
          </button>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn sm" onClick={discard} disabled={busy}>
              Verwerfen
            </button>
            <button className="btn sm primary" onClick={takeOver} disabled={busy}>
              Übernehmen
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}
